import React, { useEffect, useState } from "react";
import client from "@/api/client";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table2, BarChart2, RefreshCw } from "lucide-react";

function ColumnCard({ col }) {
  const nullColor = col.null_pct > 20 ? "#DC2626" : col.null_pct > 5 ? "#D97706" : "#059669";
  const typeColor = { number: "#2563EB", date: "#7C3AED", email: "#059669", url: "#D97706", text: "#6B7280" };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className="font-semibold text-gray-900 text-sm font-mono">{col.column}</p>
          <span className="text-[10px] px-1.5 py-0.5 rounded font-semibold mt-0.5 inline-block"
            style={{ background: `${typeColor[col.inferred_type] || "#6B7280"}15`, color: typeColor[col.inferred_type] || "#6B7280" }}>
            {col.inferred_type}
          </span>
        </div>
        <div className="text-right text-xs text-gray-400">
          <div>{col.total} rows</div>
          <div style={{ color: nullColor }}>{col.null_pct}% null</div>
        </div>
      </div>

      {/* Null bar */}
      <div className="h-1.5 bg-gray-100 rounded-full mb-3 overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${100 - col.null_pct}%`, background: "#006080" }} />
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs mb-3">
        <div className="bg-[#F8F9FA] rounded p-1.5"><span className="text-gray-400">Unique</span><br /><strong>{col.unique_count}</strong> ({col.unique_pct}%)</div>
        <div className="bg-[#F8F9FA] rounded p-1.5"><span className="text-gray-400">Avg Length</span><br /><strong>{col.avg_length}</strong> chars</div>
        {col.numeric_stats?.min !== undefined && (
          <>
            <div className="bg-[#F8F9FA] rounded p-1.5"><span className="text-gray-400">Min</span><br /><strong>{col.numeric_stats.min}</strong></div>
            <div className="bg-[#F8F9FA] rounded p-1.5"><span className="text-gray-400">Max</span><br /><strong>{col.numeric_stats.max}</strong></div>
          </>
        )}
      </div>

      {col.top_values?.length > 0 && (
        <div>
          <p className="text-xs text-gray-400 mb-1">Top values</p>
          <div className="flex flex-wrap gap-1">
            {col.top_values.slice(0, 3).map(({ value, count }) => (
              <span key={value} className="text-[10px] bg-gray-50 border border-gray-200 rounded px-1.5 py-0.5 truncate max-w-[100px]">
                {value} <span className="text-gray-400">({count})</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function DataProfilerPage() {
  const [uploads, setUploads] = useState([]);
  const [selectedUpload, setSelectedUpload] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    client.get("/files/").then(r => {
      setUploads(r.data);
      if (r.data.length > 0) setSelectedUpload(r.data[0]._id);
    });
  }, []);

  const loadProfile = async () => {
    if (!selectedUpload) return;
    setLoading(true);
    try {
      const res = await client.get(`/profiler/${selectedUpload}/profile`);
      setProfile(res.data);
    } catch (e) { setProfile(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (selectedUpload) loadProfile(); }, [selectedUpload]);

  return (
    <div className="p-6 max-w-6xl mx-auto fade-in">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1"><Table2 size={18} className="text-amber-600" /><h1 className="text-2xl font-bold text-gray-900">Data Profiler</h1></div>
          <p className="text-gray-500 text-sm">Column-level data quality statistics before processing</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedUpload} onValueChange={setSelectedUpload}>
            <SelectTrigger className="w-56"><SelectValue placeholder="Select file..." /></SelectTrigger>
            <SelectContent>{uploads.map(u => <SelectItem key={u._id} value={u._id}>{u.filename}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={loadProfile} disabled={loading}><RefreshCw size={14} className={loading ? "animate-spin" : ""} /></Button>
        </div>
      </div>

      {loading && <div className="grid grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="h-48 bg-white border border-gray-200 rounded-lg animate-pulse" />)}</div>}

      {profile && !loading && (
        <>
          <div className="flex items-center gap-4 mb-5 text-sm text-gray-500">
            <span><strong className="text-gray-900">{profile.total_rows.toLocaleString()}</strong> total rows</span>
            <span><strong className="text-gray-900">{profile.sampled_rows.toLocaleString()}</strong> sampled</span>
            <span><strong className="text-gray-900">{profile.columns?.length}</strong> columns</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {profile.columns?.map(col => <ColumnCard key={col.column} col={col} />)}
          </div>
        </>
      )}

      {!profile && !loading && (
        <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
          <BarChart2 size={40} className="mx-auto text-gray-200 mb-3" />
          <p className="text-gray-400">Select a file to profile its columns</p>
        </div>
      )}
    </div>
  );
}
